import {
  useContext,
  useMemo,
  useState,
} from 'react';

// MATERIAL UI ============================================================================================================
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

// UUID ===================================================================================================================
import { v4 as uuid } from 'uuid';

// COMPONENTS =============================================================================================================
import ImageModal from '../ImageModal/ImageModal';
import ModalComponent from '../../ModalComponent';
import LinkComponent from '../../Link/LinkComponent';
import ImagePreview from './components/ImagePreview';
import ImageScaleSlider from './components/ImageScaleSlider';
import ToggleFixedAspect from './components/ToggleFixedAspect';
import FixedAspectOptions from './components/FixedAspectOptions';
import DynamicAspectOptions from './components/DynamicAspectOptions';
import ImageBorderRadiusSlider from './components/ImageBorderRadiusSlider';

// DATA ===================================================================================================================
import LinkData from '../../../models/LinkData';
import BlockData from '../../../models/BlockData';
import { URLOpenType } from '../../../models/URLOpenType';
import ImageSettings, { Size } from '../../../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import { DynamicAspect, ImageAspectRatio, ImageOrientation } from '../../Blocks/BlockImageList/ImageSettings';

// UTILS ==================================================================================================================
import config, { getFixedAspectRatioValue } from '../../../config';
import { fitParent } from '../../../utils/image';

// HOOKS ==================================================================================================================
import { useGetBase64Image } from '../../../hooks/UseGetBase64Image';

// CONTEXT ================================================================================================================
import { FileContext } from '../../../contexts/FileContext';
import { BlockHeight } from '../../../models/BlockSettings';

// CONSTANTS ==============================================================================================================
enum Crop {
  LANDSCAPE_16_9 = 'landscape-16:9',
  LANDSCAPE_4_3 = 'landscape-4:3',
  SQUARE = 'square-1:1',
  PORTRAIT_16_9 = 'portrait-16:9',
  PORTRAIT_4_3 = 'portrait-4:3',
  CIRCLE = 'circle-1:1',
  LANDSCAPE_OVAL_16_9 = 'landscape-oval-16:9',
  LANDSCAPE_OVAL_4_3 = 'landscape-oval-4:3',
  PORTRAIT_OVAL_16_9 = 'portrait-oval-16:9',
  PORTRAIT_OVAL_4_3 = 'portrait-oval-4:3',
}

const CropOrientedAspect: Record<Crop, Size> = {
  [Crop.LANDSCAPE_16_9]: { width: 533, height: 300, borderRadius: '0' },
  [Crop.LANDSCAPE_4_3]: { width: 400, height: 300, borderRadius: '0' },
  [Crop.SQUARE]: { width: 300, height: 300, borderRadius: '0' },
  [Crop.PORTRAIT_4_3]: { width: 225, height: 300, borderRadius: '0' },
  [Crop.PORTRAIT_16_9]: { width: 168, height: 300, borderRadius: '0' },

  [Crop.CIRCLE]: { width: 300, height: 300, borderRadius: '50%' },

  [Crop.LANDSCAPE_OVAL_16_9]: { width: 533, height: 300, borderRadius: '60' },
  [Crop.LANDSCAPE_OVAL_4_3]: { width: 400, height: 300, borderRadius: '60' },
  [Crop.PORTRAIT_OVAL_4_3]: { width: 255, height: 300, borderRadius: '60' },
  [Crop.PORTRAIT_OVAL_16_9]: { width: 168, height: 300, borderRadius: '60' },
};

// INTERFACES ============================================================================================================
interface ImageSettingsModalProps {
  image?: BlockComponentImage;
  disabled: boolean;
  blockHeight?: BlockHeight;
  children?: JSX.Element;
  blockData?: BlockData[];
  onConfirm: (image: BlockComponentImage) => void;
  showModal?: boolean;
  closeModal?: () => void;
  moveable?: boolean;
  noScaleControls?: boolean;
  noRadiusControls?: boolean;
  useFixedAspectRatio?: boolean;
  noAspectRatioControls?: boolean;
  isEditing: boolean,
  hasLogo?: boolean,
}

// MAIN COMPONENT ========================================================================================================
const ImageSettingsModal = ({
  closeModal,
  showModal: propShowModal,
  image: blockComponentImage,
  disabled,
  children,
  blockHeight,
  blockData = [],
  onConfirm,
  moveable = true,
  noScaleControls = false,
  noRadiusControls = false,
  useFixedAspectRatio = false,
  noAspectRatioControls = false,
  isEditing = false,
  hasLogo = false,
}: ImageSettingsModalProps) => {
  // STATES ==============================================================================================================
  const [firstLoad, setFirstLoad] = useState(true);
  const [localShowModal, setLocalShowModal] = useState(false);
  const [imageUpdated, setImageUpdated] = useState<number>(0);
  const [updateBorderRadius, setUpdateBorderRadius] = useState(false);
  const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });
  const [image, setImage] = useState<BlockComponentImage>(blockComponentImage);
  const [scale, setScale] = useState<number>(image.settings.scale ?? 1);
  const [borderRadius, setBorderRadius] = useState<string>(image.settings.size.borderRadius ?? '0px');

  // COMPONENT CONTEXT ===================================================================================================
  const { getDownloaddableFileUrl } = useContext(FileContext);

  // COMPONENT HOOKS =====================================================================================================
  const base64Image = useGetBase64Image(image.value);

  // COMPONENT CONSTANTS =================================================================================================
  const setModalShow = setLocalShowModal;
  const modalShow = typeof propShowModal === 'boolean' ? propShowModal : localShowModal;

  // HANDLERS ============================================================================================================
  // ITEM SETTINGS
  const handleChangeFixedAspect = (fixedAspect: boolean) => {
    if (fixedAspect !== null) {
      const newImage = { ...image };
      newImage.settings.fixedAspect = fixedAspect;
      if (!fixedAspect) {
        newImage.settings.dynamicAspect = DynamicAspect.Auto;
      }
      setImage(newImage);
    }
  };

  const handleChangeDynamicAspect = (dynamicAspect: DynamicAspect) => {
    const newImage = { ...image };
    newImage.settings.dynamicAspect = dynamicAspect;
    setImage(newImage);
  };

  const handleChangeImageSettings = (settings: ImageSettings) => {
    setImage({ ...image, settings: { ...settings } });
  };

  const handleChangeOrientation = (orientation: ImageOrientation) => {
    const newImage = { ...image };
    newImage.settings.orientation = orientation;
    newImage.settings.size.borderRadius = '0';
    setBorderRadius('0');
    setUpdateBorderRadius(false);
    handleChangeImageSettings(newImage.settings);
  };

  const handleChangeAspectRatio = (aspectRatio: ImageAspectRatio) => {
    const newImage = { ...image };
    newImage.settings.aspectRatio = aspectRatio;
    setImage(newImage);
  };

  // IMAGE SETTINGS
  const handleChangeImage = (value: string) => {
    setImage({ ...image, value });
  };

  const handleChangeZoom = (scale: number) => {
    image.settings.scale = scale;
    setScale(scale);
  };

  const handleChangeBorderRadius = (newBorderRadius: string) => {
    setBorderRadius(newBorderRadius);
    setUpdateBorderRadius(true);
    // const imgSettings = { ...image.settings };
    image.settings.size.borderRadius = newBorderRadius;
    // handleChangeImageSettings(imgSettings);
  };

  const handleLinkEnable = (isEnabled: boolean) => {
    if (isEnabled) {
      image.link = LinkData.getDefault();
      setImage({ ...image });
    } else {
      image.link = undefined;
      setImage({ ...image });
    }
  };

  const handleEnableViewer = (isEnabled: boolean) => {
    if (isEnabled) {
      image.settings.enableViewer = isEnabled;
      image.uniqueId = uuid();
    } else {
      image.settings.enableViewer = undefined;
      image.uniqueId = undefined;
    }

    setImage({ ...image });
  };

  const handleChangeAlternativeText = (alt: string) => {
    setImage({ ...image, alt });
  };

  const handleConfirm = () => {
    image.settings.scale = scale;
    image.settings.size.borderRadius = borderRadius;
    onConfirm(image);
    setFirstLoad(false);
    setModalShow(false);
    if (closeModal) {
      closeModal();
    }
  };

  const selectedAspectRatio = useMemo(() => {
    if (image.settings.fixedAspect) {
      if (image.settings.orientation === ImageOrientation.Square) return ImageAspectRatio.OneByOne;
      if (image.settings.orientation === ImageOrientation.Circle) return ImageAspectRatio.OneByOne;
      if (image.settings.aspectRatio === ImageAspectRatio.SixteenByNine) return ImageAspectRatio.SixteenByNine;
      if (image.settings.aspectRatio === ImageAspectRatio.FourByThree) return ImageAspectRatio.FourByThree;
    } else if (image.settings.dynamicAspect === DynamicAspect.Square) return ImageAspectRatio.OneByOne;

    return ImageAspectRatio.OneByOne;
  }, [image]);

  const selectedOrientation = useMemo(() => {
    if (image.settings.fixedAspect) return image.settings.orientation;
    if (image.settings.dynamicAspect === DynamicAspect.Square) return ImageOrientation.Square;

    return ImageOrientation.Landscape;
  }, [image]);

  const cropSize = useMemo(() => {
    if (useFixedAspectRatio) {
      const aspectRatio = getFixedAspectRatioValue(blockHeight);

      return {
        width: Math.round(250 / aspectRatio),
        height: 250,
        borderRadius: '0',
      };
    }

    if (!image.settings?.fixedAspect && image.settings?.dynamicAspect === DynamicAspect.Auto) {
      return fitParent({ width: 605, height: 340 }, imageSize, { width: 72, height: 40 });
    }

    let cropOrientedAspect = CropOrientedAspect[`${selectedOrientation}-${selectedAspectRatio}` as Crop];
    if (updateBorderRadius) {
      cropOrientedAspect = {
        ...cropOrientedAspect,
        borderRadius,
      };
      setBorderRadius(cropOrientedAspect.borderRadius);
      setUpdateBorderRadius(false);
    }

    return cropOrientedAspect;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSize, image.settings?.dynamicAspect, image.settings?.fixedAspect, selectedAspectRatio, selectedOrientation, borderRadius]);

  const setDefaultSettings = (image: BlockComponentImage) => {
    const settings = ImageSettings.getDefault();
    setImage({ ...image, settings });
    setImageUpdated((imageUpdated + 1));
    setScale(1);
  };

  const handleDefaultImage = (image: BlockComponentImage) => {
    setImage({ ...image, value: config.defaultImageUrl });
  };

  const onCloseModal = () => {
    setModalShow(false);
    setFirstLoad(true);

    if (closeModal) {
      closeModal();
    }
  };

  // RENDER =================================================================================================================
  return (
    <>
      {isEditing ? (
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: hasLogo ? 'left' : 'center',
          }}
          onClick={(event) => {
            if (disabled) return;
            setModalShow(true);
            event.stopPropagation();
          }}
        >
          {children}
        </Box>
      ) : (
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: hasLogo ? 'left' : 'center',
          }}
        >
          {children}
        </Box>
      )}

      {isEditing && (
        <ModalComponent
          size="xl"
          title="画像設定"
          confirmButtonText="保存する"
          cancelButtonText="閉じる"
          onConfirm={handleConfirm}
          show={modalShow}
          closeModal={onCloseModal}
          centered
          animation={false}
          scrollable
          contentClassName="fullViewportHeight"
        >
          <div className="row">
            <div className="col-xl-5">
              <Card sx={{ width: '100%' }} className="mb-3">
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ height: '42px', display: 'flex', alignItems: 'center' }}>
                      <Typography className="col-form-label mb-1 text-sm" sx={{ fontWeight: '700' }}>
                        alt（代替テキスト）の設定
                      </Typography>
                    </Box>
                    <TextField
                      label="画像が表示されない時の代替テキスト"
                      defaultValue={image.alt}
                      onChange={(event) => handleChangeAlternativeText(event.target.value)}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
              {!noScaleControls
                && (
                  <Card sx={{ width: '100%' }}>
                    <CardContent>
                      {!noAspectRatioControls && (
                        <>
                          <Grid item sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <ToggleFixedAspect
                                value={image.settings.fixedAspect}
                                handleChange={handleChangeFixedAspect}
                              />
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => setDefaultSettings(image)}
                                sx={{ marginTop: '10px', marginBottom: '10px' }}
                              >
                                デフォルトに戻す
                              </Button>
                            </Box>
                          </Grid>

                          <Divider sx={{ width: '100%', my: 1 }} />

                          {image.settings.fixedAspect ? (
                            <FixedAspectOptions
                              orientation={image.settings.orientation}
                              aspectRatio={image.settings.aspectRatio}
                              handleChangeOrientation={handleChangeOrientation}
                              handleChangeAspectRatio={handleChangeAspectRatio}
                            />
                          ) : (
                            <DynamicAspectOptions value={image.settings.dynamicAspect} handleChange={handleChangeDynamicAspect} />
                          )}
                        </>
                      )}

                      <Divider sx={{ width: '100%', my: 1 }} />

                      <ImageScaleSlider
                        value={scale}
                        handleChange={handleChangeZoom}
                      />

                      {!noRadiusControls && (
                        <>
                          {[ImageOrientation.Square, ImageOrientation.Landscape, ImageOrientation.Portrait].includes(image.settings.orientation) && (
                            <ImageBorderRadiusSlider
                              value={Number(borderRadius)}
                              handleChange={handleChangeBorderRadius}
                            />
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>

                )}
            </div>

            <div className="col-xl-7">
              <div className="row g-3 mb-3">
                {noScaleControls ? (
                  <Box>
                    <img
                      src={base64Image}
                      alt={image.alt}
                      style={{ width: '100%', height: 'auto' }}
                    />
                  </Box>
                ) : (
                  <ImagePreview
                    image={image}
                    scale={scale}
                    crop={cropSize}
                    moveable={moveable}
                    handleChange={(settings) => {
                      handleChangeImageSettings(settings);
                    }}
                    handleImageSizeChange={setImageSize}
                    key={imageUpdated}
                  />
                )}
              </div>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <ImageModal
                    handleOnConfirm={handleChangeImage}
                    title="画像を選択"
                    absoluteButton={false}
                  />
                  <div className="d-flex flex-column">
                    <FormControlLabel
                      label="この画像にリンクを設定する"
                      control={(
                        <Checkbox
                          checked={!!image.link}
                          onChange={(event) => handleLinkEnable(event.target.checked)}
                        />
                      )}
                    />
                    <FormControlLabel
                      label="クリックで拡大表示する"
                      control={(
                        <Checkbox
                          checked={!!image.settings.enableViewer}
                          onChange={(event) => handleEnableViewer(event.target.checked)}
                        />
                      )}
                    />
                  </div>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleDefaultImage(image)}
                  sx={{ marginTop: '10px', marginBottom: '10px', minWidth: 127 }}
                >
                  初期画像に戻す
                </Button>
              </div>
              <Card>
                <CardContent>
                  {image.link !== undefined && blockData && (
                    <LinkComponent
                      link={image.link}
                      blockData={blockData}
                      firstLoad={firstLoad}
                      hideRemoveButton
                      hideTitle
                      onChangeLink={(url) => {
                        image.link.linkData.url = url;
                        setImage({ ...image });
                        setFirstLoad(false);
                      }}
                      onSelectBlock={(url) => {
                        image.link.linkData.url = url;
                        setImage({ ...image });
                        setFirstLoad(false);
                      }}
                      onSelectUrl={(url) => {
                        image.link.linkData.url = url;
                        setImage({ ...image });
                        setFirstLoad(false);
                      }}
                      onChangeTargetType={(targetType) => {
                        image.link.linkData.targetType = targetType;
                        setImage({ ...image });
                        setFirstLoad(false);
                      }}
                      onChangeOpenType={(checked) => {
                        image.link.linkData.openType = checked ? URLOpenType.NEW_TAB : URLOpenType.SAME_PAGE;
                        setImage({ ...image });
                        setFirstLoad(false);
                      }}
                      onChangeFile={(file) => {
                        image.link.linkData.url = getDownloaddableFileUrl(file?.file_name);
                        setImage({ ...image });
                        setFirstLoad(false);
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default ImageSettingsModal;
